import { Box, Button, Divider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';

import { CreateVisitFormValues } from '../../organisms/create-visit/schema/create-visit-validation-schema';

import { ListCard } from '@/components/atoms/list-card';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { CompactPagination } from '@/components/organisms/compact-pagination';
import { DiscountTemplate } from '@/features/hosts/api/types/discount';
import { useHostsWithSearch } from '@/features/hosts/hooks/query/use-hosts-with-search';
import {
  CreateVisitContext,
  CreateVisitEvent,
} from '@/features/visits/utils/machine/create-visit-machine';
import { useKey } from '@/hooks/use-key';
import { usePagination } from '@/hooks/use-pagination';
import { getLabel } from '@/utils/get-label';

interface Props {
  context: CreateVisitContext;
  send: (event: CreateVisitEvent) => void;
}

export const HostInfo = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createVisit']);
  const { control, getValues, setValue } = useFormContext<CreateVisitFormValues>();

  const {
    changePage,
    changePerPage,
    page,
    perPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const [query, setQuery] = useState('');

  const { data, isLoading, isFetching, error } = useHostsWithSearch({
    page,
    perPage,
    email: query,
  });

  const handleChangeQuery = (value: string) => {
    setQuery(value);
    changePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data, setTotalItems]);

  const syncVisitorDiscounts = (
    discountTemplates: DiscountTemplate[],
    clearParkingAccess = false,
  ) => {
    const availableDiscounts = new Set(discountTemplates.map(discount => discount.id));

    const visitors = getValues('visitors').map(visitor => {
      const hasAvailableDiscount =
        !clearParkingAccess &&
        visitor.discountTemplateId !== null &&
        availableDiscounts.has(visitor.discountTemplateId);

      return {
        ...visitor,
        discountTemplateId: hasAvailableDiscount ? visitor.discountTemplateId : null,
        hasAccessToParking: clearParkingAccess ? false : visitor.hasAccessToParking,
      };
    });

    setValue('visitors', visitors, { shouldDirty: true, shouldValidate: true });
  };

  const nextStep = () => {
    send({ type: 'NEXT' });
  };

  useKey('Enter', nextStep);

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          gap: 2,
        }}>
        <Typography color="textPrimary" sx={{ my: 2 }} variant="h6">
          {t('common:step')} {context.step}. {t(context.content)}
        </Typography>
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t(context.content)}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Query
              searchAsType={true}
              disabled={false}
              onChange={newQuery => handleChangeQuery(newQuery)}
              value={query}
            />
          </Box>
          <Controller
            control={control}
            name="host"
            render={({ field: { value, onChange } }) => {
              return (
                <Box sx={{ my: 2 }}>
                  {data?.resources?.map(
                    ({
                      id,
                      firstName,
                      lastName,
                      email,
                      phoneNumber,
                      destinations,
                      allowInviteToParking,
                      discountTemplates,
                    }) => {
                      const isSelected = value?.id === id;
                      const hostName = getLabel({
                        values: [firstName, lastName],
                        fallback: email || '-',
                      });
                      const hostDestinations = destinations.map(({ id, name, description }) => ({
                        id,
                        name,
                        description,
                      }));

                      const handleHostSelect = () => {
                        setValue('destinations', []);
                        syncVisitorDiscounts(
                          isSelected ? [] : discountTemplates,
                          isSelected || !allowInviteToParking,
                        );

                        if (isSelected) {
                          onChange(null);
                        } else {
                          onChange({
                            id,
                            name: hostName,
                            allowInviteToParking,
                            destinations: hostDestinations,
                            discountTemplates,
                          });
                        }
                      };

                      return (
                        <ListCard
                          key={id}
                          name={firstName}
                          secondaryName={lastName}
                          description={email}
                          rightText={phoneNumber}
                          isSelected={isSelected}
                          showCheckbox={true}
                          onClick={handleHostSelect}
                        />
                      );
                    },
                  )}
                  <DataStatusIndicator
                    dataLength={data?.resources?.length ?? 0}
                    isFetching={isLoading}
                    error={error?.response?.data?.message}
                  />
                </Box>
              );
            }}
          />
          <Divider sx={{ mt: 'auto' }} />
          <CompactPagination
            page={page}
            perPage={perPage}
            totalPages={totalPages}
            changePage={changePage}
            disabled={isLoading}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            isFetching={isFetching}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 'auto',
            pt: 3,
          }}>
          <Button color="primary" size="large" type="button" variant="contained" onClick={nextStep}>
            {t('common:button.nextStep')}
          </Button>
        </Box>
      </Box>
    </div>
  );
};
